<template>
    <!-- "Our Services" cards, managed in the admin (Website & App → Services). Hidden until the
         feed answers, and gone entirely when every card is switched off. -->
    <div v-if="services.length" class="s-py-50-100">
        <div class="container-fluid">
            <div class="max-w-[1720px] mx-auto">
                <div class="max-w-xl mx-auto mb-8 md:mb-12 text-center" data-aos="fade-up">
                    <span class="text-sm uppercase tracking-[0.2em] text-primary">What we do</span>
                    <h3 class="leading-none mt-4 text-2xl md:text-3xl">Our Services</h3>
                    <p class="mt-3">From a single made-to-measure piece to a complete fit-out, one workshop takes care of it.</p>
                </div>

                <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8 xl:gap-10" data-aos="fade-up" data-aos-delay="100">
                    <article
                        v-for="service in services"
                        :key="service.id"
                        class="group relative cursor-pointer overflow-hidden bg-[#f5f5f5] dark:bg-dark-secondary px-8 py-12 md:px-10 md:py-16 text-center transition duration-500 ease-out hover:bg-white dark:hover:bg-[#1f2a36] hover:shadow-[0_28px_60px_-24px_rgba(23,36,48,0.28)] motion-safe:hover:-translate-y-2"
                    >
                        <img
                            :src="watermark"
                            alt=""
                            aria-hidden="true"
                            class="pointer-events-none absolute inset-0 h-full w-full object-cover opacity-[0.07] grayscale transition duration-500 group-hover:scale-105 group-hover:opacity-[0.13] dark:opacity-[0.04] dark:group-hover:opacity-[0.07]"
                        />
                        <!-- Gold rule that grows out from the centre of the bottom edge on hover -->
                        <span
                            aria-hidden="true"
                            class="pointer-events-none absolute inset-x-0 bottom-0 h-[3px] origin-center scale-x-0 bg-[#BB976D] transition-transform duration-500 ease-out group-hover:scale-x-100"
                        />
                        <div class="relative">
                            <div class="mx-auto flex h-32 w-32 items-center justify-center transition duration-500 ease-out motion-safe:group-hover:-translate-y-1 motion-safe:group-hover:scale-110">
                                <img
                                    v-if="service.image"
                                    :src="service.image"
                                    :alt="service.title"
                                    class="max-h-28 max-w-28 object-contain"
                                />
                                <img
                                    v-else-if="isCustomIcon(service.icon)"
                                    :src="customIconUrl(service.icon)"
                                    :alt="service.title"
                                    class="h-32 w-32 object-contain"
                                />
                                <i
                                    v-else
                                    :class="['mdi', `mdi-${service.icon || 'sofa-outline'}`]"
                                    class="text-primary text-[68px] leading-none"
                                    aria-hidden="true"
                                />
                            </div>
                            <h4 class="mt-6 text-xl md:text-[26px] font-bold leading-tight text-title dark:text-white transition-colors duration-300 group-hover:text-[#BB976D]">{{ service.title }}</h4>
                            <p class="mt-4 text-base md:text-lg leading-relaxed text-paragraph dark:text-white">{{ service.description }}</p>
                        </div>
                    </article>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { onMounted, ref } from 'vue';
import { storefrontRequest } from '@/lib/storefront-api';
import watermark from '@/assets/img/home-v1/choose-us-bg.jpg';

// Line icons drawn for this site, served from public/images/services/{name}.svg. Keys must match
// Service::CUSTOM_ICONS on the API side; anything else in `icon` is a Material Design Icons name.
const CUSTOM_ICONS = new Set([
    'interior-design', 'architecture-design', 'residential-design', 'commercial-design', 'kitchen-design', 'landscape-design',
]);
const isCustomIcon = (name) => CUSTOM_ICONS.has(name);
const customIconUrl = (name) => `${process.env.BASE_URL || '/'}images/services/${name}.svg`;

const services = ref([]);

onMounted(async () => {
    try {
        const response = await storefrontRequest('/services');
        services.value = (response.data || []).map((service) => ({
            id: service.id,
            title: service.title,
            description: service.description,
            icon: service.icon,
            image: service.image_url,
        }));
    } catch (error) {
        services.value = [];
    }
});
</script>
